// packages/web/src/components/service/ServiceHeader.tsx

import { Box, Chip, Typography } from '@mui/material';
import React from 'react';

import { GeographicRestrictionsBanner } from './GeographicRestrictionsBanner';
import { InstallDateDisplay } from './InstallDateDisplay';
import type { MarketplaceService } from '../../types/pricing';
import { BrandLogo } from '../common/BrandLogo';
import MarkdownContent from '../common/MarkdownContent';

const ServiceHeader: React.FC<{ service: MarketplaceService }> = ({ service }) => {
  const useProgressiveDisclosure = Boolean((service as any)?.metadata?.progressive_disclosure);
  const description = useProgressiveDisclosure
    ? service.description.split(/\n\s*\n/)[0]
    : service.description;

  return (
    <Box sx={{ mb: 4 }}>
      {/* Brand banner row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {(service.brand_logo_url || service.brand_logo_path) && (
          <BrandLogo
            src={service.brand_logo_url || service.brand_logo_path}
            alt={`${service.brand_name} logo`}
            size={48}
            lazy={false}
            prefetch={true}
          />
        )}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {service.brand_name}
          </Typography>
          {/* Category breadcrumb */}
          <Typography variant="caption" color="text.secondary">
            Marketplace › {service.category}
            {(service as any).metadata?.subcategory ? ` › ${(service as any).metadata.subcategory}` : ''}
          </Typography>
        </Box>
        {/* Category chip */}
        <Chip
          label={service.category}
          size="small"
          variant="outlined"
          sx={{ ml: 'auto', borderColor: '#93c5fd', color: '#3b82f6', fontWeight: 500 }}
        />
      </Box>

      {/* Service name */}
      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ lineHeight: 1.2 }}>
        {service.name}
      </Typography>

      {/* Description */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <MarkdownContent content={description} />
      </Box>

      {/* Install date */}
      <InstallDateDisplay service={service} />

      {/* Geographic restrictions */}
      {service.geographic_restrictions &&
        (service.geographic_restrictions as any).type !== 'everywhere' && (
          <Box sx={{ mt: 2 }}>
            <GeographicRestrictionsBanner restrictions={service.geographic_restrictions} />
          </Box>
        )}
    </Box>
  );
};

export default React.memo(ServiceHeader);
